import dot from 'dot-prop-immutable';
import { pathOr } from 'ramda';

import { PAGE_REQUEST, type AppAction } from 'store/app/actions';
import type { RootState } from 'store/configureStore';
import type { ActionOfType } from 'store/types';

import {
  types,
  type ChatTasksAction,
  type TaskAssignmentSetPayload,
} from './actions';

type ChatTasksState = {
  /** keyed by task id */
  editingTasks: Record<string, boolean>;
  newTaskAssignment: TaskAssignmentSetPayload;
};

const initialState: ChatTasksState = {
  editingTasks: {},
  newTaskAssignment: {
    assignedUserFullName: null,
    assignedUserId: null,
    dueDate: null,
  },
};

type CombinedAction =
  | ChatTasksAction
  | ActionOfType<AppAction, typeof PAGE_REQUEST>;

const reducer = (
  state: ChatTasksState = initialState,
  action: CombinedAction,
): ChatTasksState => {
  switch (action.type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.EDIT_TASK_ERROR:
    case types.EDIT_TASK_SUCCESS:
      return {
        ...state,
        // `taskId` is undefined for EDIT_TASK_ERROR (see `editTask.error`),
        // which makes `dot.set` throw.
        editingTasks: dot.set(
          state.editingTasks,
          action.payload.taskId as number,
          false,
        ),
      };
    case types.EDIT_TASK_REQUEST:
      return {
        ...state,
        editingTasks: dot.set(state.editingTasks, action.payload.taskId, true),
      };
    case types.TASK_ASSIGNMENT_RESET:
      return dot.merge(state, 'newTaskAssignment', {
        assignedUserFullName: null,
        assignedUserId: null,
        dueDate: null,
      });
    case types.TASK_ASSIGNMENT_SET:
      return dot.merge(state, 'newTaskAssignment', {
        assignedUserFullName: action.payload.assignedUserFullName,
        assignedUserId: action.payload.assignedUserId,
        dueDate: action.payload.dueDate,
      });
    default:
      return state;
  }
};

const getScope = (state: RootState): ChatTasksState =>
  state.modules.common.chat.tasks;

const getNewTaskAssignmentScope = (state: RootState) =>
  getScope(state).newTaskAssignment;

export const getIsEditingTask = (state: RootState, taskId: number): boolean =>
  pathOr(false, ['editingTasks', taskId], getScope(state));

export const getTaskAssignmentUserFullName = (state: RootState) =>
  getNewTaskAssignmentScope(state).assignedUserFullName;

export const getTaskAssignmentUserId = (state: RootState) =>
  getNewTaskAssignmentScope(state).assignedUserId;

export const getTaskAssignmentDueDate = (state: RootState) =>
  getNewTaskAssignmentScope(state).dueDate;

export default reducer;
